import { Box, Center, Flex, Stack, Text, useMantineColorScheme, useMatches } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

import { PlaceholderValue, TextSerif } from "@/components/elements";
import { defaultCurrency } from "@/lib/currency";
import { formatCurrency } from "@/utils/formatEuro";

import type { Currency } from "@/types/currency";

const dummyValues = [45300, 54000, 170000, 85000, 220000];

const RecentCarsEmptyState = () => {
  const { t } = useTranslation();

  const isMobile = useMatches({ base: true, md: false });

  /* Theme */
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";
  const textColor = isDark ? "#a7a7a7A" : "secondary.4";
  const mobileDummyBGColor = isDark ? "#49494949" : "#e0e0e07b";

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  return (
    <>
      <Stack
        gap="xs"
        w="100%"
        style={{
          opacity: isDark ? 0.2 : 0.3,
        }}
      >
        {!isMobile && (
          <Flex w="100%" justify="flex-end">
            <TextSerif size="xs" c={textColor}>
              {t("market.table.cols.current_valuation")}
            </TextSerif>
          </Flex>
        )}

        <Stack gap="sm" w="100%">
          {dummyValues.map((value, index) => (
            <Flex key={index} align="center" justify="space-between" h={42} w="100%" gap="sm">
              <Flex justify="start" align="center" gap="md" w="fit-content">
                <PlaceholderValue lines={[{ w: 48, h: 38 }]} />
                <PlaceholderValue
                  secondaryColor={isMobile ? mobileDummyBGColor : undefined}
                  lines={[index === 2 ? { w: 80, h: 8 } : { w: 120, h: 8 }, { w: 80, h: 6 }]}
                />
              </Flex>

              {isMobile ? (
                <Box ml="auto" w={90}>
                  <PlaceholderValue
                    w={90}
                    lines={[{ w: 80, h: 8 }]}
                    secondaryColor={mobileDummyBGColor}
                  />
                </Box>
              ) : (
                <Box ta="right" miw={90} style={{ flexShrink: 0 }}>
                  <Text fz="sm">{formatCurrency(value, currency.code)}</Text>
                </Box>
              )}
            </Flex>
          ))}
        </Stack>
      </Stack>

      <Center pos="absolute" top={40} right={0} left={0} bottom={0} style={{ zIndex: 1 }}>
        <Stack gap="md" ta="center">
          <Stack gap="sm">
            <Text fz="h3">{t("market.last_car_viewed")}</Text>
            <TextSerif
              fz="sm"
              c={isDark ? "#999999" : "#6A6A84"}
              maw={250}
              style={{
                textWrap: "pretty",
                lineHeight: "normal",
              }}
            >
              {t("market.no_recent_cars")}
            </TextSerif>
          </Stack>
        </Stack>
      </Center>
    </>
  );
};

export default RecentCarsEmptyState;
